import React, { useState, useEffect, useRef } from 'react';
import {
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    Paper, Button, IconButton, Chip, Box, Typography, CircularProgress,
    LinearProgress, Backdrop, Pagination, Dialog, DialogContent,
    DialogActions, Alert
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import AddIcon from '@mui/icons-material/Add';
import ClonePageModal from '../components/modalWindows/ClonePageModal';
import LandingDeleteDialog from '../components/modalWindows/LandingDeleteDialog';
import getStoredValue from '../services/UseLocalStorage';
import { NAME_HEADER_AUTH } from '../utils/Constants';
import {
    fetchLandingPages,
    fetchUserDomains,
    prepareResources,
    createLanding,
    startClone,
    pollClone,
    updateLandingUrls,
    deactivateLanding,
} from '../api/pageClonerApi';

// Kept for compatibility with the legacy helper below; new cloning uses the API module.
const GEN_API_BASE = process.env.REACT_APP_CLONE_API || 'http://127.0.0.1:8000';
const POLL_TIMEOUT_MS = 35 * 60 * 1000;
const POLL_INTERVAL_MS = 5000;
const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

// ── Generate API config ─────────────────────────────────────────────────────
// Single source of truth for the clone/generate backend base URL — was
// previously hardcoded as a local "testLocalHost" const inline at each call
// site, shadowing GEN_API_BASE and making local/prod impossible to switch
// without editing multiple files. Override via .env.local: REACT_APP_CLONE_API=...
// Backend's own hard per-job ceiling is 30 min (_SINGLE_PASS_JOB_TIMEOUT in
// backend/main.py); the UI poll timeout must exceed it or the UI gives up on
// a job the backend would still resolve (with a clean TIMEOUT failure).
const CLONE_ESTIMATE_SEC   = 5 * 60;         // countdown starts at 05:00

// Reassuring messages that rotate during the wait
const CLONE_MESSAGES = [
    "Analizando la estructura de la captura…",
    "Identificando secciones y elementos de diseño…",
    "Redactando el copy en español para tu producto…",
    "Generando imágenes con inteligencia artificial…",
    "Subiendo assets a S3…",
    "Ensamblando el HTML final…",
    "Casi listo, optimizando los últimos detalles…",
];

const formatHHMMSS = (totalSeconds) => {
    const h = Math.floor(totalSeconds / 3600).toString().padStart(2, '0');
    const m = Math.floor((totalSeconds % 3600) / 60).toString().padStart(2, '0');
    const s = (totalSeconds % 60).toString().padStart(2, '0');
    return `${h}:${m}:${s}`;
};

/** Polls GET /status/{jobId} until stage is "done" or "error" (or timeout).
 *  Response shape: { stage, detail, url?, error? }. */
async function pollJob(jobId) {
    const deadline = Date.now() + POLL_TIMEOUT_MS;
    while (Date.now() < deadline) {
        await sleep(POLL_INTERVAL_MS);
        const res = await fetch(`${GEN_API_BASE}/status/${jobId}`);
        const data = await res.json();
        if (data.stage === "done") return data;                 // { stage, detail, url }
        if (data.stage === "error") throw new Error(data.error || "El clonado falló");
        // queued / running stages → keep looping
    }
    throw new Error("La generación está tardando más de lo esperado (35 minutos). El proceso puede seguir en curso: vuelve a intentarlo en unos minutos antes de generar una nueva copia.");
}

const PagesView = () => {
    const [pages, setPages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1); // 1-based
    const rowsPerPage = 7;
    const [totalRows, setTotalRows] = useState(0);
    const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [serverError, setServerError] = useState(null);
    const [availableDomains, setAvailableDomains] = useState([]);
    const [domainsLoading, setDomainsLoading] = useState(false);
    const [isCloningInBackground, setIsCloningInBackground] = useState(false);
    const [cloneSecondsLeft, setCloneSecondsLeft] = useState(CLONE_ESTIMATE_SEC);
    const [cloneMsgIndex, setCloneMsgIndex] = useState(0);
    const [deleteDialog, setDeleteDialog] = useState({
        open: false,
        row: null,
        status: 'confirm'
    });
    const countdownRef = useRef(null);
    const msgTimerRef  = useRef(null);
    const editorToken = getStoredValue(NAME_HEADER_AUTH);

    // Countdown + rotating messages while cloning
    useEffect(() => {
        if (isCloningInBackground) {
            setCloneSecondsLeft(CLONE_ESTIMATE_SEC);
            setCloneMsgIndex(0);

            // Tick every second, clamp at 0
            countdownRef.current = setInterval(() => {
                setCloneSecondsLeft((s) => (s > 0 ? s - 1 : 0));
            }, 1000);

            // Rotate messages every ~40 s so all 7 show during the ~5-minute window
            msgTimerRef.current = setInterval(() => {
                setCloneMsgIndex((i) => (i + 1) % CLONE_MESSAGES.length);
            }, 40000);
        } else {
            clearInterval(countdownRef.current);
            clearInterval(msgTimerRef.current);
        }
        return () => {
            clearInterval(countdownRef.current);
            clearInterval(msgTimerRef.current);
        };
    }, [isCloningInBackground]);


    const fetchPages = async (page = 1) => {
        try {
            setLoading(true);
            if (!editorToken) {
                console.error("No auth token found");
                setLoading(false);
                return;
            }
            const data = await fetchLandingPages(page, rowsPerPage);
            setPages(Array.isArray(data.items) ? data.items : []);
            setTotalRows(Number(data.TotalRows) || 0);
        } catch (error) {
            console.error("Fetch error:", error);
        } finally {
            setLoading(false);
        }
    };


    useEffect(() => {
        fetchPages(currentPage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [currentPage]);

    useEffect(() => {
        let cancelled = false;
        const loadDomains = async () => {
            if (!editorToken) return;
            setDomainsLoading(true);
            try {
                const data = await fetchUserDomains();
                if (!cancelled) setAvailableDomains(Array.isArray(data.data) ? data.data : []);
            } catch (error) {
                console.error('Domain fetch error:', error);
                if (!cancelled) setAvailableDomains([]);
            } finally {
                if (!cancelled) setDomainsLoading(false);
            }
        };
        loadDomains();
        return () => { cancelled = true; };
    }, [editorToken]);

    const handleGenerate = async (formData) => {
            setIsSubmitting(true);
            setServerError(null);
            setIsCloningInBackground(true); // Show progress bar immediately
            setIsCloneModalOpen(false); // Hide modal immediately after user clicks Clone
        try {
            const effectiveDomainMode = formData.domainMode === 'NONE'
                ? 'PLATFORM'
                : formData.domainMode;
            const effectiveSubdomain = effectiveDomainMode === 'PLATFORM'
                ? (formData.subdomain || formData.pageName)
                : formData.subdomain;

            const preparation = await prepareResources({
                domain: formData.domain,
                domainMode: effectiveDomainMode,
                subdomain: effectiveSubdomain,
            });

            const {
                workspace,
                domain,
                domainRequired,
                domainCreated,
            } = preparation;

            if (domainRequired && !domain) {
                throw new Error('No se pudo preparar el dominio seleccionado');
            }

            const domainId = domain?.ID_Dominios_Usuarios ?? null;

            const landingData = await createLanding({
                name: formData.pageName,
                sourceUrl: formData.url,
                domainId,
            });
            const uuidLanding = landingData.data?.uuid_landing || landingData.uuid_landing;
            const landingDataInfo = landingData.data || landingData;
            const s3Prefix = landingDataInfo.s3Prefix;

            if (!uuidLanding) throw new Error("No se pudo obtener el ID de la landing");
            if (!s3Prefix) throw new Error("No se recibió el prefijo S3 de la landing");

            console.info('Resources prepared', {
                workspaceCreated: preparation.workspaceCreated,
                domainRequired,
                domainCreated,
                domainId,
            });

            // 5. Start the async clone job: JSON POST /clone → {job_id} (or {error}, 400).
            const cloneStart = await startClone({
                sourceUrl: formData.url,
                productName: formData.productName,
                productPrice: formData.productPrice,
                landingUuid: uuidLanding,
                workspace,
                s3Prefix,
                cloudfrontDomain: workspace?.URL_CloudFront,
            });
            const { job_id } = cloneStart;

            // 5b. Poll GET /status/{job_id} every 5 s until done or error
            const genResult = await pollClone(job_id);
            const s3UrlFinal = genResult.url;
            if (!s3UrlFinal) throw new Error("No se recibió la URL final del generador");

            // 6. Update Landings_Usuarios with final S3 and CloudFront URLs
            const objectPath = `${String(s3Prefix).replace(/^\/+/, '')}index.html`;
            const selectedFqdn = preparation.domain?.Dominio
                || preparation.customerDomain?.fqdn
                || preparation.platformDomain?.fqdn;
            const selectedDomainUrl = selectedFqdn
                ? `https://${String(selectedFqdn).replace(/^https?:\/\//i, '').replace(/\/$/, '')}/${objectPath}`
                : null;
            const workspaceUrl = workspace?.URL_CloudFront
                ? `${workspace.URL_CloudFront.replace(/\/$/, '')}/${objectPath}`
                : null;
            const updateRes = await updateLandingUrls({
                landingUuid: uuidLanding,
                s3Url: s3UrlFinal,
                cloudFrontUrl: selectedDomainUrl || workspaceUrl || s3UrlFinal,
                status: "READY",
                error: null,
            });
            if (!updateRes.ok) throw new Error("Error actualizando la URL final de la página");

            // Success — stop overlay, refresh table
            setIsCloningInBackground(false);
            fetchPages(1); // After clone, go to first page

        } catch (error) {
            console.error("Clone flow error:", error);
            setIsCloningInBackground(false); // Stop progress bar on error too
            const errorMsg = error.message.includes("Failed to fetch")
                ? "Error de conexión. No se pudo generar la página. Inténtelo de nuevo."
                : error.message.includes("support@pulpoia.com")
                    ? error.message
                    : `${error.message}. Comuníquese con support@pulpoia.com o con nuestro representante de Telegram.`;
            setServerError(errorMsg);
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleDelete = (row) => {
        setDeleteDialog({
            open: true,
            row,
            status: 'confirm'
        });
    };

    const handleCloseDeleteDialog = () => {
        setDeleteDialog({
            open: false,
            row: null,
            status: 'confirm'
        });
    };

    const handleConfirmDelete = async () => {
        if (!deleteDialog.row?.uuid_landing) {
            alert('No se pudo identificar la landing page a borrar');
            handleCloseDeleteDialog();
            return;
        }

        setDeleteDialog((prev) => ({
            ...prev,
            status: 'loading'
        }));

        try {
            await deactivateLanding(deleteDialog.row.uuid_landing);
            await fetchPages(currentPage);

            setDeleteDialog((prev) => ({
                ...prev,
                status: 'success'
            }));
        } catch (error) {
            console.error('Delete error:', error);
            handleCloseDeleteDialog();
            alert('Error al intentar eliminar la página');
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '';
        const date = new Date(dateString);
        const day = date.getDate().toString().padStart(2, '0');
        const month = (date.getMonth() + 1).toString().padStart(2, '0');
        const year = date.getFullYear();
        const hours = date.getHours().toString().padStart(2, '0');
        const minutes = date.getMinutes().toString().padStart(2, '0');
        return `${day}/${month}/${year} ${hours}:${minutes}`;
    };

    // Style constants to match the dark theme
    const colors = {
        background: '#1a1d27',
        surface: '#252a36',
        headerBg: '#2d3342',
        textPrimary: '#ffffff',
        textSecondary: '#9ca3af',
        link: '#38bdf8',
        activeBg: '#064e3b',
        activeText: '#34d399',
        border: '#374151'
    };

    // Calculate empty rows to always show 7
    const emptyRows = Math.max(0, rowsPerPage - pages.length);

    // Pagination handler
    const handlePageChange = (event, value) => {
        setCurrentPage(value);
    };

    const handleCloseErrorDialog = () => {
        setServerError(null);
    };

    const handleAcknowledgeErrorDialog = async () => {
        setServerError(null);
        await fetchPages(currentPage);
    };

    return (
        <Box sx={{ padding: '40px', backgroundColor: colors.background, minHeight: '100vh', color: colors.textPrimary }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' }}>
                <Typography variant="h4" sx={{ fontWeight: 'bold' }}>Mis Páginas</Typography>
                <Button 
                    variant="contained" 
                    startIcon={<AddIcon sx={{ color: '#000000', fill: '#000000' }} />}
                    onClick={() => setIsCloneModalOpen(true)}
                    sx={{ 
                        backgroundColor: '#ffffff', 
                        color: '#000000', 
                        fontWeight: 'bold',
                        textTransform: 'none',
                        borderRadius: '8px',
                        '&:hover': { backgroundColor: '#e5e7eb' }
                    }}
                >
                    Clonar nueva página
                </Button>
            </Box>

            {/* ── Full-screen cloning overlay ─────────────────────────── */}
            <Backdrop
                open={isCloningInBackground}
                sx={{
                    zIndex: 1400,
                    backgroundColor: 'rgba(15, 17, 26, 0.92)',
                    backdropFilter: 'blur(6px)',
                    flexDirection: 'column',
                    gap: 3,
                }}
            >
                {/* Spinner */}
                <CircularProgress size={64} thickness={3} sx={{ color: colors.link }} />

                {/* Countdown */}
                <Typography
                    variant="h2"
                    sx={{
                        fontWeight: 700,
                        fontVariantNumeric: 'tabular-nums',
                        color: cloneSecondsLeft > 0 ? colors.link : '#34d399',
                        letterSpacing: 4,
                    }}
                >
                    {cloneSecondsLeft > 0 ? formatHHMMSS(cloneSecondsLeft) : '00:00:00'}
                </Typography>

                {/* Status label */}
                <Typography variant="subtitle1" sx={{ color: colors.textSecondary, fontWeight: 500 }}>
                    {cloneSecondsLeft > 0 ? 'Tiempo estimado restante' : 'Casi listo, finalizando…'}
                </Typography>

                {/* Rotating message */}
                <Typography
                    variant="body2"
                    sx={{
                        color: colors.textSecondary,
                        maxWidth: 420,
                        textAlign: 'center',
                        minHeight: 40,
                    }}
                >
                    {CLONE_MESSAGES[cloneMsgIndex]}
                </Typography>

                {/* Progress bar */}
                <Box sx={{ width: 340 }}>
                    <LinearProgress
                        sx={{
                            height: 6,
                            borderRadius: 3,
                            backgroundColor: colors.headerBg,
                            '& .MuiLinearProgress-bar': { backgroundColor: colors.link },
                        }}
                    />
                </Box>

                <Typography variant="caption" sx={{ color: '#4b5563', mt: 1 }}>
                    No cierre esta ventana mientras se genera la página.
                </Typography>
            </Backdrop>


            <TableContainer component={Paper} sx={{ backgroundColor: colors.surface, borderRadius: '12px', overflow: 'hidden' }}>
                <Table>
                    <TableHead>
                        <TableRow sx={{ backgroundColor: colors.headerBg }}>
                            <TableCell sx={{ color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }}>Mis Clones</TableCell>
                            <TableCell sx={{ color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }}>Página Original</TableCell>
                            <TableCell sx={{ color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }}>Status</TableCell>
                            <TableCell sx={{ color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }}>Creada el día</TableCell>
                            <TableCell sx={{ color: colors.textSecondary, borderBottom: `1px solid ${colors.border}`, width: '150px' }}></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {loading ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 5, borderBottom: 'none' }}>
                                    <CircularProgress sx={{ color: colors.link }} />
                                </TableCell>
                            </TableRow>
                        ) : pages.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={4} align="center" sx={{ py: 5, color: colors.textSecondary, borderBottom: 'none' }}>
                                    No hay páginas clonadas aún.
                                </TableCell>
                            </TableRow>
                        ) : (
                            <>
                                {pages.map((row, index) => (
                                    <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                                        <TableCell sx={{ color: colors.link, borderBottom: `1px solid ${colors.border}` }}>
                                            {row.URL_cloudFront_final || row.URL_s3_final ? (
                                                <a
                                                    href={row.URL_cloudFront_final || row.URL_s3_final}
                                                    target="_blank"
                                                    rel="noopener noreferrer"
                                                    style={{ color: 'inherit', textDecoration: 'none' }}
                                                >
                                                    {row.URL_cloudFront_final || row.URL_s3_final}
                                                </a>
                                            ) : (
                                                <span style={{ color: colors.textSecondary }}>
                                                    URL pendiente
                                                </span>
                                            )}
                                        </TableCell>

                                        <TableCell sx={{ borderBottom: `1px solid ${colors.border}` }}>
                                            {/^https?:\/\//.test(row.Landing_URL || '') ? (
                                                <a href={row.Landing_URL} target="_blank" rel="noopener noreferrer" style={{ color: colors.link, textDecoration: 'none' }}>
                                                    {row.Landing_URL}
                                                </a>
                                            ) : (
                                                <span style={{ color: colors.textSecondary }}>
                                                    {row.Landing_URL || '—'}
                                                </span>
                                            )}
                                        </TableCell>

                                        <TableCell sx={{ borderBottom: `1px solid ${colors.border}` }}>
                                            {row.Landing_Active === 1 ? (
                                                <Chip 
                                                    label="Activa" 
                                                    size="small"
                                                    sx={{ 
                                                        backgroundColor: colors.activeBg, 
                                                        color: colors.activeText,
                                                        fontWeight: 'bold',
                                                        borderRadius: '4px'
                                                    }} 
                                                />
                                            ) : (
                                                <Chip 
                                                    label="Inactiva" 
                                                    size="small"
                                                    sx={{ 
                                                        backgroundColor: '#7f1d1d', 
                                                        color: '#fca5a5',
                                                        fontWeight: 'bold',
                                                        borderRadius: '4px'
                                                    }} 
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ color: colors.textSecondary, borderBottom: `1px solid ${colors.border}` }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <AccessTimeIcon fontSize="small" />
                                                {formatDate(row.Landing_CreatedAt)}
                                            </Box>
                                        </TableCell>
                                        <TableCell sx={{ borderBottom: `1px solid ${colors.border}` }}>
                                            <Box sx={{ display: 'flex', gap: 1 }}>
                                                <IconButton 
                                                    size="small" 
                                                    component="a" 
                                                    href={`https://app.pulpoia.com/pulpo-cloner/?id=${row.uuid_landing}&oen=${encodeURIComponent(editorToken || '')}`} 
                                                    target="_blank"
                                                    sx={{ color: colors.textSecondary, '&:hover': { color: colors.textPrimary } }}
                                                >
                                                    <EditIcon fontSize="small" />
                                                </IconButton>
                                                <IconButton 
                                                    size="small"
                                                    onClick={() => handleDelete(row)}
                                                    sx={{ color: colors.textSecondary, '&:hover': { color: '#ef4444' } }}
                                                >
                                                    <DeleteIcon fontSize="small" />
                                                </IconButton>
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {/* Empty rows to always show 7 */}
                                {Array.from({ length: emptyRows }).map((_, idx) => (
                                    <TableRow key={`empty-${idx}`}>
                                        <TableCell colSpan={5} sx={{ borderBottom: `1px solid ${colors.border}`, height: 53, backgroundColor: colors.surface }} />
                                    </TableRow>
                                ))}
                            </>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* Pagination below the grid */}
            <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                <Pagination
                    count={Math.max(1, Math.ceil(totalRows / rowsPerPage))}
                    page={currentPage}
                    onChange={handlePageChange}
                    color="primary"
                    shape="rounded"
                    size="large"
                    showFirstButton
                    showLastButton
                    sx={{
                        '& .MuiPaginationItem-root': {
                            color: '#ffffff'
                        },
                        '& .MuiPaginationItem-root.Mui-selected': {
                            color: '#ffffff'
                        },
                        '& .MuiPaginationItem-root.Mui-disabled': {
                            color: '#ffffff',
                            opacity: 1
                        }
                    }}
                />
            </Box>

            <ClonePageModal
                open={isCloneModalOpen}
                onClose={() => setIsCloneModalOpen(false)}
                onClone={handleGenerate}
                isSubmitting={isSubmitting}
                domains={availableDomains}
                domainsLoading={domainsLoading}
            />

            <Dialog
                open={Boolean(serverError)}
                onClose={handleCloseErrorDialog}
                fullWidth
                maxWidth="sm"
            >
                <DialogContent sx={{ pt: 3 }}>
                    <Alert severity="error" variant="filled">
                        {serverError}
                    </Alert>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button onClick={handleAcknowledgeErrorDialog} sx={{ textTransform: 'none' }}>
                        Aceptar
                    </Button>
                </DialogActions>
            </Dialog>

            <LandingDeleteDialog
                open={deleteDialog.open}
                status={deleteDialog.status}
                onConfirm={handleConfirmDelete}
                onCancel={handleCloseDeleteDialog}
                onClose={handleCloseDeleteDialog}
            />
        </Box>
    );
};

export default PagesView;
